import {useCallback, useContext, useEffect, useRef, useState} from 'react';

import {useCookies} from 'react-cookie';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';

import {Grid, Typography, AlertColor} from '@mui/material';

import EmpionLogo from '../../../../components/EmpionLogoAnimation/EmpionLogo';
import {SnackBarContext} from '../../../../contexts/SnackBarContext/SnackBarContext';
import {UserContext} from '../../../../contexts/UserContext/UserContext';
import {useApiWrite} from '../../../../hooks/useApiWrite';
import {Post} from '../../../../lib/api/POST/Post';
import {PostRoutes} from '../../../../lib/api/POST/PostRoutes';
import {EMPION_COOKIE_ID} from '../../../../lib/constants';

export const VerifyPage = () => {
  const navigate = useNavigate();
  const {dispatchMessage} = useContext(SnackBarContext);
  const {t} = useTranslation();
  const [cookies, setCookie] = useCookies([EMPION_COOKIE_ID]);
  const requestSentRef = useRef(false);
  const {currentUser} = useContext(UserContext);
  const [accountConfirmedState, setAccountConfirmedState] = useState(false);

  const handleSnackbar = useCallback(
    (message: string, severity: AlertColor) => {
      dispatchMessage(message, severity);
    },
    [dispatchMessage]
  );

  const searchParams = new URLSearchParams(window.location.search);
  const userId: string = searchParams.get('id') || '';
  const authToken: string = searchParams.get('token') || '';

  const {makeRequest, error} = useApiWrite<Post.VerifyUser>({
    method: 'post',
    path: PostRoutes[Post.VerifyUser](userId, authToken),
    onSuccess: () => {
      setAccountConfirmedState(true);
      handleSnackbar(t('verification.successMessage'), 'success');
      if (!currentUser) {
        navigate('/login');
        return;
      }
      const currentCookieValue = cookies[EMPION_COOKIE_ID];
      const updatedCookieValue = JSON.stringify({
        ...currentCookieValue,
        accountConfirmed: true,
      });
      setCookie(EMPION_COOKIE_ID, updatedCookieValue, {
        path: '/',
        domain: process.env.REACT_APP_COOKIE_DOMAIN,
        httpOnly: false,
      });
      navigate('/');
    },
  });

  useEffect(() => {
    if (!requestSentRef.current && !currentUser?.accountConfirmed) {
      makeRequest(undefined);
      requestSentRef.current = true;
    }

    if (currentUser?.accountConfirmed) {
      setAccountConfirmedState(true);
      handleSnackbar(t('verification.accountAlreadyVerified'), 'success');
      navigate('/');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, t, handleSnackbar]);

  useEffect(() => {
    if (error) {
      if (error.extra_details === 'Account already confirmed') {
        setAccountConfirmedState(true);
        handleSnackbar(t('verification.accountAlreadyVerified'), 'success');
        if (!currentUser) {
          navigate('/login');
        } else {
          navigate('/');
        }
        return;
      }
      handleSnackbar(t('verification.errorMessage'), 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, error, handleSnackbar, currentUser]);

  return (
    <Grid container justifyContent={'center'}>
      <Grid item xs={10} display={'flex'} justifyContent={'center'}>
        <EmpionLogo />
      </Grid>
      <Grid item xs={12} marginTop={3}>
        <Typography align='center' variant='h3'>
          {t(`verification.${accountConfirmedState ? 'headerMessage' : 'verifying'}`)}
        </Typography>
      </Grid>
    </Grid>
  );
};
